import { LeagueConfig, Environment } from '@leagueplatform/core';
import { createReactRouterAdapter } from '@leagueplatform/react-router-adapter';
import { APP_ID } from 'common/constants';
import {
  getEnvironmentDefinition,
  getPersistedEnvironmentKey,
} from 'common/environments';
import { LoadingSpinner } from 'components/loading-spinner/loading-spinner.component';
import EN_LOCALES from 'locales/en.json';
import ES_LOCALES from 'locales/es.json';
import FR_LOCALES from 'locales/fr.json';
import LiveChatAgentAvatar from 'assets/live-chat-agent-avatar.svg';
import LeaveSteppedActivityIcon from 'assets/leave-stepped-activity.png?url';
import MessagingEmptyStateIcon from 'assets/messaging-empty-state.png?url';
import MessagingErrorIcon from 'assets/messaging-error.png?url';
import { JourneyDelightOverlayAnimation } from 'components/journey-delight-overlay-animation/journey-delight-overlay-animation.component';
import { eOSHealthDemoLightTheme } from 'theme/theme';
import { aiAgentAssistBotLiveChatClient } from '@leagueplatform/ai-agent-assist-bot';
import { getCaregiverImpersonationState } from '@leagueplatform/caregiver';
import { liveChatClient } from './clients/eos-live-chat-client';

const GUEST_OUTREACH_ENV = getEnvironmentDefinition('test4-ca');

const isGuestOutreachRoute =
  typeof window !== 'undefined' &&
  window.location.pathname.startsWith('/guest/');

const activeEnvironment = isGuestOutreachRoute
  ? GUEST_OUTREACH_ENV
  : getEnvironmentDefinition(getPersistedEnvironmentKey());

const authConfig = {
  domain: activeEnvironment.auth0Domain,
  client_id: activeEnvironment.auth0ClientId,
  audience: activeEnvironment.auth0Audience,
  redirect_uri: window.location.origin,
  // Attaches caregiver impersonation state (act-as-care-recipient) to auth
  // requests. Evaluated once at module load; this is safe because entering or
  // leaving impersonation triggers a full page reload (via the caregiver SDK),
  // so a fresh module evaluation always reflects the current state. No-op when
  // not impersonating. Mirrors the SDK Presenter wiring.
  ...getCaregiverImpersonationState(),
};

// The Auth0 `connection` is a `redirectLoginOptions` default rather than a
// client-level option: it has to reach the SDK's signed-out auto-redirect
// (`useAuth`'s `redirectToLogInWhenSignedOut`), which sends no connection of its
// own, while leaving silent renewal (`getTokenSilently` → `prompt=none`) alone.
// A connection-less `/authorize` leaves Auth0 to resolve the connection itself,
// and on the reference tenant it resolves to `Testing-DB` — which holds no member
// accounts, so the login form rejects valid credentials.
//
// MSP2-107 removed this default deliberately, when caregivers still had their own
// MFA-enforcing `Caregivers` connection: a pinned `Member-DB` could not reuse a
// `Caregivers` session and bounced caregivers to a member login. MSP2-174 moved
// caregivers into the member database, so there is no second session type left to
// reuse, and `Member-DB` does not enforce MFA.
const redirectLoginOptions = activeEnvironment.auth0Connection
  ? { connection: activeEnvironment.auth0Connection }
  : {};

const apiConfig = {
  wsUrl: activeEnvironment.wsApiUrl,
  url: activeEnvironment.apiUrl,
  legacyRestApi: activeEnvironment.legacyRestApiUrl,
};

export const config: LeagueConfig = {
  core: {
    clientId: APP_ID,
    auth: {
      clientOptions: authConfig,
      redirectLoginOptions,
      idleSessionLifetimeHours: 0.25, // Default 15 minutes, should match the auth0 session inactivity timeout
    },
    api: apiConfig,
    appEnvironment: activeEnvironment.appEnvironment as Environment,
    contentUrl: activeEnvironment.contentUrl,
    routing: { adapter: createReactRouterAdapter() },
    ui: {
      theme: eOSHealthDemoLightTheme,
      components: {
        pageHeader: {
          shape: 'line',
        },
        loading: {
          element: <LoadingSpinner />,
          text: false,
        },
      },
    },
    i18n: {
      strings: {
        default: EN_LOCALES,
        en: EN_LOCALES,
        es: ES_LOCALES,
        fr: FR_LOCALES,
      },
    },
  },
  assets: {
    overrides: {
      LIVE_CHAT_AVATAR: LiveChatAgentAvatar,
      WAVE_GOODBYE: LeaveSteppedActivityIcon,
      MESSAGING_EMPTY_MESSAGES: MessagingEmptyStateIcon,
      MESSAGING_ERROR: MessagingErrorIcon,
      ACTIVITIES_CYCLING_ANIMATION: JourneyDelightOverlayAnimation,
    },
  },
  claims: {
    enableDynamicFilters: true,
    featureFlags: {
      shouldShowClaimDetails: true,
    },
  },
  benefits: {
    benefitsLandingMasonryConfigKey: 'benefits$landingPage$root',
    benefitsDetailsMasonryConfigKey: 'benefits$detailPage$root',
  },
  careDiscovery: {
    masonryAppVersion: '2.0',
    masonryAppId: {
      left: 'careLandingPage$root',
    },
  },
  careDocuments: {
    shouldUseDocumentsV2: true,
  },
  careFinder: {
    providerComparisonWebRenderersEnabled: true,
    schedulingEnabled: true,
  },
  dashboard: {
    metricDetailsMasonryConfigKey: 'dashboard$metricDetails$root',
    allRecordedDataMasonryConfigKey: 'dashboard$allRecordedData$root',
  },
  liveChat: {
    liveChatClient,
    aiAgentAssistBotLiveChatClient,
  },
  healthJourney: {
    activityDetailsConfig: {
      enableFullPageBackground: true,
    },
  },
  wallet: {
    showLandingPageClaimsEntry: true,
  },
  __internal: {
    shouldApplyCSSReset: true,
    shouldApplyLegacyGlobalStyles: true,
    shouldIgnoreDOMLangAttributeForLocale: true,
  },
};
